import React, { useCallback, useMemo, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import AdminLayout from "./layouts/adminLayout/AdminLayout";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import Pos from "./views/pos/Pos";
import ADMINROUTES from "./routes/admin/admin.Route";
import { renderRoutes } from "./utils/routing/renderRoutes";
import AdminLogin from "./views/login/AdminLogin";
import BRANCHROUTES from "./routes/branch/branch.Route";
import BranchLogIn from "./views/login/BranchLogIn";
import RestaurantLayout from "./layouts/restaurantLayout/RestaurantLayout";
import AllTable from "./branch/views/restaurant/allTable/AllTable";
import MyTable from "./branch/views/restaurant/myTable/MyTable";
import NotFound from "./branch/pages/notfound/NotFound";
import { getDecryptedToken } from "./utils/crypto/cryptoUtils";
import Forbidden from "./branch/pages/notfound/Forbidden";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Marts from "./views/marts/Marts";
import BranchLayout from "./layouts/baranchLayout/BranchLayout";
import PrintBill from "./branch/pages/settings/printbill/PrintBill";
import ReceiptNew from "./views/print/ReceiptNew";
import Kot from "./branch/views/kot/Kot";
import CustomerView from "./views/customerview/CustomerView";
import CustomerViewNew from "./views/pos/utils/CustomerView";
import CompanyLayout from "./layouts/companyLayout/CompanyLayout";
import COMPANYROUTES from "./routes/company/company.Route";
import CompanyLogin from "./views/login/CompanyLogin";
import ForgotPassword from "./views/login/ForgotPassword";
import ResetPassword from "./views/login/ResetPassword";
import { initializePosSocketListeners } from "./store/pos/posSockets";
import PaymentView from "./views/paymentview/PaymentView";

// Create React Query client with optimized configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // Data stays fresh for 5 minutes
      gcTime: 10 * 60 * 1000, // Cache data for 10 minutes (formerly cacheTime)
      retry: 1, // Retry failed requests once
      refetchOnWindowFocus: false, // Don't refetch on window focus
      refetchOnReconnect: true, // Refetch when reconnecting
    },
  },
});

const user = getDecryptedToken("user");
const token = getDecryptedToken("token");
const permissionRoutes = getDecryptedToken("routeAccess");

const companyUser = getDecryptedToken("user");
const companyToken = getDecryptedToken("company_token");

const App = () => {
  useEffect(() => {
    initializePosSocketListeners();
  }, []);
  const filterByIds = useCallback((array: any, ids: any) => {
    return array?.reduce((filtered: any, item: any) => {
      if (ids?.includes(item?.KEY)) {
        const newItem = { ...item };

        if (item.SUBNAV) {
          newItem.SUBNAV = filterByIds(item?.SUBNAV, ids);
        }
        if (item.SUBSUBNAV) {
          newItem.SUBSUBNAV = filterByIds(item.SUBSUBNAV, ids);
        }
        filtered.push(newItem);
      }
      return filtered;
    }, []);
  }, []);

  const adminAccess = useMemo(
    () => filterByIds(ADMINROUTES, permissionRoutes),
    [filterByIds],
  );
  const branchAccess = useMemo(
    () => filterByIds(BRANCHROUTES, permissionRoutes),
    [filterByIds],
  );

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route
            path="/"
            element={
              <Navigate to={user === "branch" ? "dashboard" : "login"} />
            }
          />
          {user === "admin" && token && (
            <Route
              path="/admin"
              element={<AdminLayout adminAccess={adminAccess} />}
            >
              <Route path="/admin" element={<Navigate to={"dashboard"} />} />
              {renderRoutes(adminAccess)}
            </Route>
          )}

          {user === "branch" && token && (
            <>
              <Route
                path="/"
                element={<BranchLayout adminAccess={branchAccess} />}
              >
                <Route path="/" element={<Navigate to={"dashboard"} />} />
                {renderRoutes(branchAccess)}
              </Route>
              <Route path="/restaurant" element={<RestaurantLayout />}>
                <Route index element={<Navigate to="alltable" />} />
                <Route path="alltable" element={<AllTable />} />
                <Route path="mytable" element={<MyTable />} />
              </Route>
              <Route path="/pos" element={<Pos />} />
              <Route path="/kot" element={<Kot />} />
              <Route path="/view/customer" element={<CustomerView />} />
              <Route path="/customer" element={<CustomerViewNew />} />
            </>
          )}

          {/* Company Routes */}
          {companyUser === "company" && companyToken && (
            <Route path="/company" element={<CompanyLayout />}>
              <Route path="/company" element={<Navigate to={"dashboard"} />} />
              {renderRoutes(COMPANYROUTES)}
            </Route>
          )}

          <Route path="/marts" element={<Marts />} />
          <Route path="/admin-login" element={<AdminLogin />} />
          <Route path="/login" element={<BranchLogIn />} />
          <Route path="/company-login" element={<CompanyLogin />} />
          <Route
            path="/admin-forgot-password"
            element={<ForgotPassword type="admin" />}
          />
          <Route
            path="/forgot-password"
            element={<ForgotPassword type="branch" />}
          />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/forbidden" element={<Forbidden />} />
          <Route path="/print" element={<PrintBill />} />
          <Route path="/bill" element={<ReceiptNew shouldPrint={true} />} />
          <Route path="/shop/pay" element={<PaymentView />} />

          <Route
            path="*"
            element={token ? <NotFound /> : <Navigate to="/login" replace />}
          />
        </Routes>
      </BrowserRouter>

      <ToastContainer
        position="bottom-right"
        autoClose={2000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        theme="light"
      />
    </QueryClientProvider>
  );
};

export default App;
